import * as React from 'react';
import { useFormikContext } from 'formik';
import { addHours, format, startOfDay, subMonths } from 'date-fns';
import { CloseRounded } from '@mui/icons-material';
import { Button } from '@mui/material';
import { Appointment, LoadedUser, Option } from '../../interfaces';
import {
  getArtistOptions,
  getArtistsForService,
} from '../../services/business';
import useUser from '../../hooks/global/useUser';
import { CheckoutFormValues } from '../../types/appointments';
import InputRow from './InputRow';
import useCalendarSelectedAppointment from '../../hooks/appointments/useCalendarSelectedAppointment';
import useGetAppointments from '../../hooks/appointments/useGetAppointments';
import { getFullName } from '../../helpers/global';
import SelectInput from './SelectInput';
import useConfirm from '../../hooks/useConfirm';

interface Props {
  appointment: Appointment | null;
  onSelectAppointment?: (appointment: Appointment | null) => void;
}

function ClearButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      type="button"
      onClick={onClick}
      sx={{ minWidth: 0, padding: 1, my: 'auto' }}>
      <CloseRounded
        style={{
          color: 'var(--darkGrey)',
          fontSize: 20,
        }}
      />
    </Button>
  );
}

export default function CheckoutDetails({
  appointment,
  onSelectAppointment,
}: Props) {
  const confirm = useConfirm();
  const { values, setFieldValue } = useFormikContext<CheckoutFormValues>();
  const { user: loadedUser } = useUser<LoadedUser>();
  const [artistOptions, setArtistOptions] = React.useState<Option[]>();

  const { selectedAppointment } = useCalendarSelectedAppointment();

  const startDate = React.useMemo(
    () => format(startOfDay(subMonths(new Date(), 1)), 'yyyy-MM-dd'),
    [],
  );
  const endDate = React.useMemo(
    () => addHours(new Date(), 1).toISOString(),
    [],
  );
  const isSinglePassedAppointment = appointment && !onSelectAppointment;
  const { appointments } = useGetAppointments({
    dataStartDate: startDate,
    dataEndDate: endDate,
    isReversed: true,
    limit: 50,
    includeCanceled: false,
    onlyIncomplete: true,
    useExactDates: true,
    customerId: values.customer.customerId,
    enabled: !selectedAppointment && !isSinglePassedAppointment,
  });
  const appointmentsList = selectedAppointment
    ? [selectedAppointment]
    : isSinglePassedAppointment
    ? [appointment]
    : appointments;

  const commissionOptions: Option[] = React.useMemo(() => {
    const employee = loadedUser.business.employees.find(
      e => e.employeeId === values.employeeId,
    );

    const tempCommissionOptions = [
      {
        label: `${(
          (employee?.paymentToEmployeePercent || 0) * 100
        ).toString()}%`,
        value: false,
      },
    ];

    if (
      employee?.payrollType === 'COMMISSION' &&
      employee?.bonusCommissionPercent
    ) {
      tempCommissionOptions.push({
        label: `${(
          Math.min(
            employee.paymentToEmployeePercent + employee.bonusCommissionPercent,
            1,
          ) * 100
        ).toString()}%`,
        value: true,
      });
    }

    return tempCommissionOptions;
  }, [loadedUser.business.employees, values.employeeId]);

  React.useEffect(() => {
    async function fetchArtistOptions() {
      if (loadedUser) {
        if (!values.serviceId) {
          const tempArtistOptions = getArtistOptions(loadedUser);
          setArtistOptions(tempArtistOptions);
        } else {
          const service = loadedUser.business.services?.find(
            tempService => tempService.serviceId === Number(values.serviceId),
          );

          if (service) {
            const tempArtistOptions = await getArtistsForService(
              loadedUser,
              service,
            );
            setArtistOptions(tempArtistOptions);
          }
        }
      }
    }

    fetchArtistOptions();
  }, [loadedUser, values.serviceId]);

  const onChangeAppt = async (_: string, value: string) => {
    if (!onSelectAppointment) return;

    const appt = appointments?.find(a => a.appointmentId === value);
    if (!appt) return;

    const itemsFromLibrary = (values.customOrderItems ?? []).filter(
      item => !!item.itemId,
    );
    if (!appt.businessId && itemsFromLibrary.length) {
      const confirmed = await confirm({
        title: 'Incompatible Items',
        message:
          'Some selected items are not compatible with this appointment. They will be removed if you continue.',
        negativeText: 'Cancel',
        positiveText: 'Continue',
      });
      if (!confirmed) return;
      setFieldValue(
        'customOrderItems',
        (values.customOrderItems ?? []).filter(item => !item.itemId),
      );
    }

    setFieldValue('appointmentId', value);
    setFieldValue('employeeId', appt.employeeId);
    setFieldValue('price', appt.price || 0);
    setFieldValue('customer.firstName', appt.customer.firstName);
    setFieldValue('customer.lastName', appt.customer.lastName);
    setFieldValue('customer.phoneNumber', appt.customer.phoneNumber);
    setFieldValue('customer.email', appt.customer.email);
    setFieldValue('customer.dob', appt.customer.dob);
    setFieldValue('customer.customerId', appt.customerId);
    setFieldValue('customer.role', appt.customer.role);

    onSelectAppointment(appt);
  };

  const onClearAppt = () => {
    if (!onSelectAppointment) return;

    setFieldValue('appointmentId', '');
    setFieldValue('employeeId', '');
    setFieldValue('price', 0);

    if (values.paymentMethod === 'FREE') {
      setFieldValue('paymentMethod', '');
    }

    onSelectAppointment(null);
  };

  const onClearEmployee = () => {
    setFieldValue('employeeId', '');
  };

  const disableEmployeeChange =
    !!appointment ||
    (loadedUser?.business?.type === 'INDEPENDENT' &&
      values.employeeId === loadedUser?.employee?.employeeId);

  return (
    <div className="input-group appointment-details">
      <div className="inputs">
        <div className="input-row">
          <SelectInput
            label="Appointment"
            name="appointmentId"
            options={(appointmentsList ?? []).map(appt => ({
              label: `${getFullName(appt.customer)} • ${format(
                new Date(appt.startDateTime!),
                'MMMM do, h:mma',
              )}`,
              value: appt.appointmentId,
            }))}
            placeholder="Select an appointment"
            autoWidth={false}
            disabled={!!selectedAppointment || !onSelectAppointment}
            values={values}
            setFieldValue={onChangeAppt}
            renderSuffix={
              appointment && !selectedAppointment && onSelectAppointment
                ? () => <ClearButton onClick={onClearAppt} />
                : undefined
            }
          />
        </div>
        {artistOptions && (
          <div className="input-row">
            <SelectInput
              options={artistOptions}
              name="employeeId"
              label="Provider"
              placeholder="Select a provider"
              disabled={disableEmployeeChange}
              autoWidth={false}
              values={values}
              setFieldValue={setFieldValue}
              renderSuffix={
                !appointment && values.employeeId && !disableEmployeeChange
                  ? () => <ClearButton onClick={onClearEmployee} />
                  : undefined
              }
            />
          </div>
        )}
        {commissionOptions && commissionOptions.length > 1 && appointment && (
          <InputRow
            formName="checkout"
            inputs={[
              {
                type: 'select',
                name: 'includeBonus',
                label: 'Service Commission',
                options: commissionOptions,
                placeholder: 'Select the artist commission',
                disabled: false,
                autoWidth: false,
              },
            ]}
            values={values}
            setFieldValue={setFieldValue}
          />
        )}
      </div>
    </div>
  );
}
